import * as React from 'react'
import { useForm, useFieldArray } from 'react-hook-form'

import {
  SittingReportStep2 as Presenter,
  IProps as IPresenter,
} from './presenter'
import { useInitialize } from '@/hooks/useInitialize'
import { parseDate, parseDateString } from '@/utils/dateFormat'
import { initSupportEvents, buildSupportEvent } from '@/utils/reserve'
import {
  tmpSaveSittingReportStep2,
  buildTmpSaveEventParams,
  buildReserveOption,
  uploadSittingPhoto,
  deleteSittingPhoto,
} from '@/apis/reserves'

interface IProps
  extends Omit<
    IPresenter,
    'minDate' | 'confirmApproveHandler' | 'confirmCancelHandler'
  > {
  reserve: IReserveSummary
  requestedChildIds: number[]
  options: IOption[]
  isTokyoBsTmpUse: boolean
  isReportBack?: boolean
  supportChildren: IChildSummary[]
  lastComment: IReserveComment
  user: IUser
  supportEvents: IEventApi[]
  showSittingPhotos: boolean
  sittingPhotos: ISittingPhoto[]
  editSittingPhotoEditUrl: string
  backUrl: string
  nextUrl: string
}

const convertToReserveSittingPhoto = (
  reserve: IReserveSummary,
  photo: ISittingPhoto,
): IReserveSittingPhoto => {
  return {
    url: photo.image.thumb.url,
    slug: photo.slug,
    editUrl: `/schedules/${reserve.slug}/sitting_photo/${photo.slug}/caption?s=1`,
    caption: photo.caption,
    takenAt: parseDate(photo.taken_at),
  }
}

export const SittingReportStep2 = ({
  reserve,
  minReportChildrenMood,
  requestedChildIds,
  options,
  isTokyoBsTmpUse,
  isReportBack,
  supportChildren,
  lastComment,
  user,
  supportEvents,
  showSittingPhotos,
  sittingPhotos,
  editSittingPhotoEditUrl,
  backUrl,
  nextUrl,
}: IProps): JSX.Element => {
  const [isInitialized] = useInitialize()
  const [showToast, setShowToast] = React.useState<boolean>(false)
  const [showError, setShowError] = React.useState<boolean>(false)
  const [errorMessages, setErrorMessages] = React.useState<string[]>([])
  const [showSupportEventModal, setSupportEventModal] =
    React.useState<boolean>(false)
  const [showPhotoUploadLoading, setShowPhotoUploadLoading] =
    React.useState<boolean>(false)
  const [showPhotoUploadMask, setShowPhotoUploadMask] =
    React.useState<boolean>(false)
  const photos = sittingPhotos.map((photo) =>
    convertToReserveSittingPhoto(reserve, photo),
  )
  const [inputSittingPhotos, setSittingPhotos] =
    React.useState<IReserveSittingPhoto[]>(photos)
  const { hour, minute } = parseDateString(reserve.actual_start_at)
  const startDate = parseDate(reserve.actual_start_at)
  const events = initSupportEvents(
    supportEvents,
    startDate,
    hour,
    minute,
    requestedChildIds,
  )
  const initialComment = isReportBack
    ? reserve.tmp_message_on_report_back_long
    : reserve.message_on_report_input_long

  const [isEdited, setIsEdited] = React.useState<boolean>(false)
  const [showConfirm, setShowConfirm] = React.useState<boolean>(false)
  const [showDeleteConfirm, setShowDeleteConfirm] =
    React.useState<boolean>(false)
  const [deleteIndex, setDeleteIndex] = React.useState<number>()
  const initOptions = options.map((option) => buildReserveOption(option))

  /*
   * Form設定
   */

  const {
    control,
    watch,
    setValue,
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ICompleteReportFormStateStep2>({
    mode: 'onBlur',
    shouldFocusError: false,
    defaultValues: {
      sittingReportChildrenMood: reserve.sitting_report_children_mood
        ? reserve.sitting_report_children_mood
        : '',
      comment: initialComment ? initialComment : '',
      tokyoBsTmpUseReport: reserve.tokyo_bs_tmp_use_report,
      supportEvents: events,
      options: initOptions,
      messageOnReportBack: reserve.message_on_report_back
        ? reserve.message_on_report_back
        : reserve.message_on_report_input_long,
    },
  })

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'supportEvents',
  })

  const inputSupportEvents = watch('supportEvents')
  const inputSittingReportChildrenMood = watch('sittingReportChildrenMood')
  const inputComment = watch('comment')
  const inputMessageOnReportBack = watch('messageOnReportBack')
  const inputTokyoBsTmpUseReport = watch('tokyoBsTmpUseReport')
  const inputOptions = watch('options')

  /*
   * トースト表示
   */
  const showErrorToast = (messages: string[]) => {
    setErrorMessages(messages)
    setTimeout(() => {
      setShowError(false)
      setErrorMessages([])
    }, 2000),
      setShowError(true)
  }

  /*
   * 送信
   */
  const isValid = async () => {
    const events = buildTmpSaveEventParams(inputSupportEvents, startDate)

    const result = await tmpSaveSittingReportStep2(
      reserve.id,
      reserve.slug,
      inputOptions,
      events,
      true,
      inputSittingReportChildrenMood,
      inputComment,
      inputTokyoBsTmpUseReport,
      inputMessageOnReportBack,
    )
    if (result.result && result.res.result) {
      window.location.href = nextUrl
    } else {
      showErrorToast(result.res.errors)
      window.scroll({
        top: 0,
        behavior: 'smooth',
      })
    }
  }

  const isInValid = () => {
    showErrorToast(['エラーを確認してください'])
    window.scroll({
      top: 0,
      behavior: 'smooth',
    })
  }

  /*
   * 前の画面に戻る
   */
  const cancelHandler = () => {
    if (isEdited) {
      setShowConfirm(true)
    } else {
      location.href = backUrl
    }
  }

  const confirmApproveHandler = () => {
    location.href = backUrl
  }

  const confirmCancelHandler = () => {
    setShowConfirm(false)
  }

  /*
   * 一時保存
   */
  const clickTmpSaveHandler = async () => {
    const events = buildTmpSaveEventParams(inputSupportEvents, startDate)

    const result = await tmpSaveSittingReportStep2(
      reserve.id,
      reserve.slug,
      inputOptions,
      events,
      false,
      inputSittingReportChildrenMood,
      inputComment,
      inputTokyoBsTmpUseReport,
      inputMessageOnReportBack,
    )
    if (result.result && result.res.result) {
      setIsEdited(false)
      setTimeout(() => {
        setShowToast(false)
      }, 2000),
        setShowToast(true)
    } else {
      showErrorToast(result.res.errors)
      window.scroll({
        top: 0,
        behavior: 'smooth',
      })
    }
  }

  /*
   * 保育内容の追加
   */
  const addSupportEventHandler = (eventType: ISupportEventType) => {
    const supportEvent = buildSupportEvent(
      eventType,
      startDate,
      hour.toString().padStart(2, '0'),
      minute.toString().padStart(2, '0'),
      requestedChildIds,
    )
    append(supportEvent, { shouldFocus: false })
    setSupportEventModal(false)
  }

  const openSupportEventHandler = () => {
    setSupportEventModal(true)
  }

  const closeSupportEventHandler = () => {
    setSupportEventModal(false)
  }

  /*
   * 保育内容の削除
   */

  const deleteSupportEventHandler = (index: number) => {
    setDeleteIndex(index)
    setShowDeleteConfirm(true)
  }

  const deleteApproveHandler = () => {
    remove(deleteIndex)
    setDeleteIndex(undefined)
    setShowDeleteConfirm(false)
  }

  const deleteCancelHandler = () => {
    setShowDeleteConfirm(false)
  }

  /*
   * サポート中の写真
   */
  const uploadSittingPhotoHandler = async (files: File[]) => {
    const photos: IReserveSittingPhoto[] = []
    let errorCount = 0

    setShowPhotoUploadMask(true)
    setShowPhotoUploadLoading(true)

    const results = await Promise.all(
      files.map((file) => uploadSittingPhoto(reserve.slug, file)),
    )
    results.forEach((result) => {
      if (result.result && result.res.result) {
        const photo = JSON.parse(result.res.photo) as ISittingPhoto
        photos.unshift(convertToReserveSittingPhoto(reserve, photo))
      } else {
        errorCount++
      }
    })

    setSittingPhotos([...photos, ...inputSittingPhotos])

    if (errorCount > 0) {
      if (errorCount === files.length) {
        alert('写真のアップロードに失敗しました。')
      } else {
        alert('一部の写真のアップロードに失敗しました。')
      }
    }

    setShowPhotoUploadLoading(false)
    setShowPhotoUploadMask(false)
  }

  const deleteSittingPhotoHandler = async (index: number) => {
    setShowPhotoUploadMask(true)
    const photo = inputSittingPhotos[index]

    const result = await deleteSittingPhoto(reserve.slug, photo.slug)
    if (result.result && result.res.result) {
      inputSittingPhotos.splice(index, 1)
      // 再レンダリングさせるため配列を作り直す
      setSittingPhotos([...inputSittingPhotos])
    } else {
      alert('削除できませんでした')
    }

    setShowPhotoUploadMask(false)
  }

  /*
   * 編集済みかのハンドリング
   */
  React.useEffect(() => {
    if (isInitialized) {
      setIsEdited(true)
    }
  }, [
    JSON.stringify(inputSupportEvents),
    inputSittingReportChildrenMood,
    inputComment,
    inputTokyoBsTmpUseReport,
  ])

  return (
    <Presenter
      isInitialized={isInitialized}
      isTokyoBsTmpUse={isTokyoBsTmpUse}
      isReportBack={isReportBack}
      control={control}
      fields={fields}
      errors={errors}
      supportChildren={supportChildren}
      user={user}
      lastComment={lastComment}
      inputSupportEvents={inputSupportEvents}
      inputSittingReportChildrenMood={inputSittingReportChildrenMood}
      inputComment={inputComment}
      inputMessageOnReportBack={inputMessageOnReportBack}
      inputSittingPhotos={inputSittingPhotos}
      minDate={startDate}
      minReportChildrenMood={minReportChildrenMood}
      showToast={showToast}
      showError={showError}
      errorMessages={errorMessages}
      showSittingPhotos={showSittingPhotos}
      showSupportEventModal={showSupportEventModal}
      showPhotoUploadLoading={showPhotoUploadLoading}
      showPhotoUploadMask={showPhotoUploadMask}
      editSittingPhotoEditUrl={editSittingPhotoEditUrl}
      setValue={setValue}
      register={register}
      submitHandler={handleSubmit(isValid, isInValid)}
      cancelHandler={cancelHandler}
      clickTmpSaveHandler={clickTmpSaveHandler}
      addSupportEventHandler={addSupportEventHandler}
      openSupportEventHandler={openSupportEventHandler}
      closeSupportEventHandler={closeSupportEventHandler}
      deleteSupportEventHandler={deleteSupportEventHandler}
      uploadSittingPhotoHandler={uploadSittingPhotoHandler}
      deleteSittingPhotoHandler={deleteSittingPhotoHandler}
      showConfirm={showConfirm}
      showDeleteConfirm={showDeleteConfirm}
      confirmApproveHandler={confirmApproveHandler}
      confirmCancelHandler={confirmCancelHandler}
      deleteApproveHandler={deleteApproveHandler}
      deleteCancelHandler={deleteCancelHandler}
    />
  )
}

export default SittingReportStep2
